/**
 * What a node of the reporting tree is.
 *
 * The root is the group itself and there is exactly one; below it a house reports
 * through a REGION (geography) or a BRAND (how it is sold), never through both — a
 * second dimension is a second tree, and the answer to "who reports to whom" has one.
 */
export enum OrgUnitKind {
    Group  = 'group',
    Region = 'region',
    Brand  = 'brand',
}

/**
 * What the scope selector is standing on.
 *
 * `Group` is everything this account may see and is the state the console opens in.
 * The other three narrow it, and each of them is what the address bar carries:
 * `?scope=unit:<id>`, `?scope=entity:<id>`, `?scope=properties:<id>,<id>`. A link
 * pasted into a message therefore opens on the same houses the sender was looking at.
 */
export enum ScopeKind {
    Group      = 'group',
    Unit       = 'unit',
    Entity     = 'entity',
    Properties = 'properties',
}

/**
 * What happens to a house's past when it changes owner (the owner's decision 1 of
 * 16. 9. 2026).
 *
 * `Stays` is the ordinary sale: what was booked under the old company was booked by the
 * old company, and the new one starts on the day of the transfer. `Follows` is the
 * reorganisation inside one holding, where the house's whole history is meant to read
 * as the new company's. Neither is a default the console may pick for somebody — the
 * dialog asks, and `Stays` is only where the radio starts.
 */
export enum TransferHistory {
    Stays   = 'stays',
    Follows = 'follows',
}

/**
 * The all-zero object id.
 *
 * On a house it reads as UNASSIGNED — no company yet, or no place in the tree — and the
 * console draws that state rather than hiding it.
 */
export const UNASSIGNED_ID = '000000000000000000000000'

/** Whether an id names something, as opposed to the zero the API sends for "nothing". */
export const isAssigned = (id?: string | null): boolean => !!id && id !== UNASSIGNED_ID
